import React, { useState, useEffect } from 'react';
import { getConfiguracionEntidad, updateConfiguracionEntidad } from '../services/configuracionEntidadService';
import { useNavigate } from 'react-router-dom';
import { Save, ArrowLeft, Settings, RefreshCw, Image as ImageIcon } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';

import { useLayout } from '../contexts/LayoutContext';

export default function ConfiguracionEntidadForm() {
    const navigate = useNavigate();
    const { refreshConfig } = useLayout();
    const [formData, setFormData] = useState({
        nombreCooperativa: '',
        ruc: '',
        direccionCasaMatriz: '',
        telefonoContacto: '',
        urlLogo: '',
        lemaCooperativa: ''
    });

    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);

    useEffect(() => {
        cargarConfiguracion();
    }, []);

    const cargarConfiguracion = async () => {
        try {
            const data = await getConfiguracionEntidad();
            setFormData(data);
        } catch (error) {
            toast.error('Error al cargar la configuración');
        } finally {
            setInitialLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            await updateConfiguracionEntidad(formData);
            await refreshConfig();
            toast.success('Configuración actualizada correctamente');
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    if (initialLoading) {
        return <div className="p-20 text-center uppercase font-black text-slate-400 tracking-[0.2em]">Cargando Configuración...</div>;
    }

    return (
        <div className="max-w-4xl mx-auto bg-white shadow-xl border border-slate-200 mb-10 overflow-hidden font-sans">
            <Toaster position="top-right" />
            <div className="bg-slate-700 p-5 flex items-center justify-between text-white">
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate('/parametrizacion')} className="p-2 hover:bg-slate-600 rounded-full transition-all" type="button">
                        <ArrowLeft className="w-6 h-6" />
                    </button>
                    <h1 className="text-xl font-bold uppercase tracking-tight">Datos de la Entidad</h1>
                </div>
                <Settings className="w-10 h-10 text-emerald-500 opacity-30" />
            </div>

            <form onSubmit={handleSubmit} className="p-10 space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2 flex justify-center mb-4">
                        <div className="relative group">
                            <div className="w-32 h-32 rounded-full border-4 border-slate-100 overflow-hidden bg-slate-50 flex items-center justify-center shadow-inner">
                                {formData.urlLogo ? (
                                    <img src={formData.urlLogo} alt="Logo" className="w-full h-full object-contain" />
                                ) : (
                                    <ImageIcon className="w-12 h-12 text-slate-300" />
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="md:col-span-2">
                        <label className="block text-[0.65rem] font-black text-slate-500 mb-1.5 uppercase tracking-widest">Nombre de la Cooperativa</label>
                        <input
                            type="text"
                            required
                            className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-sm font-bold uppercase text-slate-800 focus:border-emerald-500 outline-none"
                            value={formData.nombreCooperativa}
                            onChange={(e) => setFormData({ ...formData, nombreCooperativa: e.target.value.toUpperCase() })}
                        />
                    </div>

                    <div>
                        <label className="block text-[0.65rem] font-black text-slate-500 mb-1.5 uppercase tracking-widest">RUC</label>
                        <input
                            type="text"
                            required
                            className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-sm font-bold text-blue-600 focus:border-emerald-500 outline-none"
                            value={formData.ruc}
                            onChange={(e) => setFormData({ ...formData, ruc: e.target.value })}
                        />
                    </div>

                    <div>
                        <label className="block text-[0.65rem] font-black text-slate-500 mb-1.5 uppercase tracking-widest">Teléfono de Contacto</label>
                        <input
                            type="text"
                            className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-sm font-medium focus:border-emerald-500 outline-none"
                            value={formData.telefonoContacto}
                            onChange={(e) => setFormData({ ...formData, telefonoContacto: e.target.value })}
                        />
                    </div>

                    <div className="md:col-span-2">
                        <label className="block text-[0.65rem] font-black text-slate-500 mb-1.5 uppercase tracking-widest">Dirección Casa Matriz</label>
                        <input
                            type="text"
                            className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-sm font-medium focus:border-emerald-500 outline-none"
                            value={formData.direccionCasaMatriz}
                            onChange={(e) => setFormData({ ...formData, direccionCasaMatriz: e.target.value })}
                        />
                    </div>

                    <div className="md:col-span-2">
                        <label className="block text-[0.65rem] font-black text-slate-500 mb-1.5 uppercase tracking-widest">Lema de la Cooperativa</label>
                        <input
                            type="text"
                            className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-sm font-medium italic focus:border-emerald-500 outline-none"
                            placeholder="Ej: Juntos por un futuro mejor"
                            value={formData.lemaCooperativa}
                            onChange={(e) => setFormData({ ...formData, lemaCooperativa: e.target.value })}
                        />
                    </div>

                    <div className="md:col-span-2">
                        <label className="block text-[0.65rem] font-black text-slate-500 mb-1.5 uppercase tracking-widest">URL del Logo</label>
                        <input
                            type="text"
                            className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-sm font-medium text-xs focus:border-emerald-500 outline-none"
                            placeholder="https://ejemplo.com/logo.png"
                            value={formData.urlLogo}
                            onChange={(e) => setFormData({ ...formData, urlLogo: e.target.value })}
                        />
                    </div>
                </div>

                <div className="pt-8 border-t flex justify-end gap-4">
                    <button type="button" onClick={() => navigate('/parametrizacion')} className="text-xs font-black uppercase text-slate-400 hover:text-slate-600">DESCARTAR</button>
                    <button type="submit" disabled={loading} className="px-10 py-3 bg-slate-800 text-white font-black text-xs uppercase tracking-[0.2em] flex items-center gap-2 transition-all hover:bg-slate-900 border-b-4 border-emerald-600">
                        {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 text-emerald-500" />}
                        GUARDAR CAMBIOS
                    </button>
                </div>
            </form>
        </div>
    );
}
